import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * The Section Rendering API needs the fully qualified section id
 * (`template--<template id>__main`), which is only visible in the rendered
 * product page. It is discovered on the first full page fetch and reused after
 * that, so later opens skip the (very large) product page document entirely.
 */
const SECTION_ID_STORAGE_KEY = 'quick-add:product-section-id';

/** @returns {string | null} */
function getKnownProductSectionId() {
  try {
    return sessionStorage.getItem(SECTION_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** @param {string} sectionId */
function rememberProductSectionId(sectionId) {
  try {
    sessionStorage.setItem(SECTION_ID_STORAGE_KEY, sectionId);
  } catch {
    // Private mode: fall back to full page fetches.
  }
}

function forgetProductSectionId() {
  try {
    sessionStorage.removeItem(SECTION_ID_STORAGE_KEY);
  } catch {
    // Nothing to clean up.
  }
}

/**
 * Shared across every product card so hovering one card, then opening the same
 * product from another card (or after a grid re-render) reuses the same response.
 * @type {Map<string, Element>}
 */
const productGridCache = new Map();

/** @type {Map<string, Promise<Element | null>>} */
const productGridRequests = new Map();

/**
 * The product the dialog is currently showing. Guards against a slow response
 * for one card landing in the modal after another card was opened.
 * @type {string | null}
 */
let activeModalUrl = null;

export class QuickAddComponent extends Component {
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // Warm the cache before the click so "選択" feels instant. pointerdown also
    // covers touch devices, where there is no hover to prefetch on.
    this.addEventListener('pointerenter', this.#prefetchProductPage, { passive: true });
    this.addEventListener('pointerdown', this.#prefetchProductPage, { passive: true });
    this.addEventListener('focusin', this.#prefetchProductPage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.removeEventListener('pointerenter', this.#prefetchProductPage);
    this.removeEventListener('pointerdown', this.#prefetchProductPage);
    this.removeEventListener('focusin', this.#prefetchProductPage);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    productGridCache.clear();
    productGridRequests.clear();
  };

  /**
   * Prefetch product HTML in the background (hover/focus/press).
   *
   * Only runs once the cheap section-rendering path is known. Before that a
   * prefetch would download the whole product page, which is far too heavy to
   * fire off every time a card is hovered.
   */
  #prefetchProductPage = () => {
    if (!getKnownProductSectionId()) return;

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;
    if (productGridCache.has(currentUrl) || productGridRequests.has(currentUrl)) return;

    void this.#getProductGrid(currentUrl);
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    variantPicker?.updateVariantPicker(newHtml);
  }

  /**
   * Whether the quick-add dialog is currently open.
   * @returns {boolean}
   */
  #isDialogOpen() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return false;
    return Boolean(dialogComponent.refs.dialog?.open);
  }

  /**
   * Shows a skeleton inside the modal while content loads.
   */
  #showLoadingState() {
    const modalContent = document.getElementById('quick-add-modal-content');
    const dialog = document.querySelector('#quick-add-dialog dialog');
    if (!modalContent) return;

    const loadingText = dialog instanceof HTMLElement ? dialog.dataset.loadingText || '' : '';

    modalContent.innerHTML = `
      <div class="quick-add-modal__loading" aria-busy="true" aria-live="polite">
        <span class="quick-add-modal__loading-text">${loadingText}</span>
        <div class="quick-add-modal__skeleton quick-add-modal__skeleton--media"></div>
        <div class="quick-add-modal__skeleton-details">
          <div class="quick-add-modal__skeleton quick-add-modal__skeleton--title"></div>
          <div class="quick-add-modal__skeleton quick-add-modal__skeleton--price"></div>
          <div class="quick-add-modal__skeleton quick-add-modal__skeleton--option"></div>
          <div class="quick-add-modal__skeleton quick-add-modal__skeleton--option"></div>
          <div class="quick-add-modal__skeleton quick-add-modal__skeleton--button"></div>
        </div>
      </div>
    `;
  }

  /**
   * Returns cached product grid, or fetches/caches it.
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #getProductGrid(productPageUrl) {
    const cached = productGridCache.get(productPageUrl);
    if (cached) return cached;

    const pending = productGridRequests.get(productPageUrl);
    if (pending) return pending;

    const fetchPromise = (async () => {
      const knownSectionId = getKnownProductSectionId();
      let gridElement = null;

      if (knownSectionId) {
        const sectionHtml = await this.fetchProductPage(productPageUrl, knownSectionId);
        gridElement = sectionHtml?.querySelector('[data-product-grid-content]') ?? null;

        // The section id belongs to another template, or the theme changed.
        if (!gridElement) forgetProductSectionId();
      }

      if (!gridElement) {
        const pageHtml = await this.fetchProductPage(productPageUrl);
        gridElement = pageHtml?.querySelector('[data-product-grid-content]') ?? null;

        const sectionId = gridElement?.closest('[id^="shopify-section-"]')?.id;
        if (sectionId) rememberProductSectionId(sectionId.replace('shopify-section-', ''));
      }

      if (!gridElement) return null;

      const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      productGridCache.set(productPageUrl, productGrid);
      return productGrid;
    })().finally(() => {
      productGridRequests.delete(productPageUrl);
    });

    productGridRequests.set(productPageUrl, fetchPromise);
    return fetchPromise;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    activeModalUrl = currentUrl;
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    // Open modal immediately with skeleton — don't wait for network
    this.#showLoadingState();
    dialogComponent.showDialog();

    /** @type {Element | null} */
    let productGrid = null;

    try {
      productGrid = await this.#getProductGrid(currentUrl);
    } catch (error) {
      console.error('Quick add: could not load product', error);
    }

    // User closed the modal, or another product was requested while waiting
    if (!this.#isDialogOpen() || activeModalUrl !== currentUrl) return;

    if (!productGrid) {
      // Never leave the customer looking at a skeleton: send them to the product page.
      this.#closeQuickAddModal();
      window.location.href = currentUrl;
      return;
    }

    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page, or just one section of it when a section id is given.
   * Never throws: callers fall back to the next strategy when this returns null.
   *
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @param {string} [sectionId] - Section Rendering API id
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl, sectionId) {
    if (!productPageUrl) return null;

    const url = new URL(productPageUrl, window.location.origin);
    if (sectionId) url.searchParams.set('section_id', sectionId);

    // Requests are deduplicated and cached, so they are never aborted: a request
    // started by a hover must stay alive for the click that follows it.
    try {
      const response = await fetch(url.toString());

      if (!response.ok) return null;

      const responseText = await response.text();

      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch {
      return null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Defer image decoding so the modal shell paints first; keep first image eager
    productGrid.querySelectorAll('img').forEach((img, index) => {
      if (img instanceof HTMLImageElement) {
        img.loading = index === 0 ? 'eager' : 'lazy';
        img.decoding = 'async';
      }
    });

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
